import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  Check,
  Copy,
  ExternalLink,
  GitBranch,
  Loader2,
  Mic,
  Search,
  Send,
  Square,
} from "lucide-react";
import { toast } from "sonner";

import { BpmnView } from "@/braining/components/bpmn-view";
import { MarkdownView } from "@/braining/components/markdown-view";
import { plainText } from "@/braining/markdown";
import {
  DIRECTIONS,
  TOPICS,
  directionLabel,
  ruDate,
  sectionsOf,
  topicLabel,
  type Corpus,
  type DirectionId,
  type RegulationsAnswer,
  type Section,
  type Source,
  type TopicId,
} from "@/braining/regulations";
import { layout, toBpmnXml } from "@/braining/regulations-bpmn";
import { askRegulationsFn, loadRegulationsFn } from "@/braining/regulations.functions";
import { useSpeech } from "@/braining/use-speech";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/**
 * Регламенты — одна витрина вместо Диска, сетевой папки, Miro и сайта Насти
 * (созвон с Викой 26.10.2026): вкладки по ходу проекта сбоку, сверху вопрос
 * текстом или голосом. Справочная отвечает только по корпусу и честно
 * говорит, где регламент молчит, — с именем, у кого уточнить.
 *
 * Состояние экрана в адресе: ссылку на раздел или схему можно переслать
 * коллеге в чат.
 */

type View = TopicId | "bpmn" | "diff";

interface RegSearch {
  t?: View;
  s?: string;
  p?: string;
  d?: DirectionId;
}

const VIEWS = new Set<string>([...TOPICS.map((t) => t.id), "bpmn", "diff"]);
const DIRS = new Set<string>(DIRECTIONS.map((d) => d.id));

export const Route = createFileRoute("/regulations")({
  head: () => ({ meta: [{ title: "Регламенты — Портал Braining Group" }] }),
  validateSearch: (q: Record<string, unknown>): RegSearch => ({
    t: typeof q.t === "string" && VIEWS.has(q.t) ? (q.t as View) : undefined,
    s: typeof q.s === "string" ? q.s : undefined,
    p: typeof q.p === "string" ? q.p : undefined,
    d: typeof q.d === "string" && DIRS.has(q.d) ? (q.d as DirectionId) : undefined,
  }),
  component: RegulationsPage,
});

function RegulationsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/regulations" });
  const [corpus, setCorpus] = useState<Corpus | null | undefined>(undefined);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  useEffect(() => {
    loadRegulationsFn()
      .then((r) => {
        setCorpus(r.corpus);
        setUpdatedAt(r.updatedAt);
      })
      .catch((e) => {
        console.error("[regulations] не загрузились", e);
        toast.error("Не удалось загрузить регламенты");
        setCorpus(null);
      });
  }, []);

  const go = (next: RegSearch) => void navigate({ search: next });
  const openSection = (id: string) => {
    const s = corpus?.sections.find((x) => x.id === id);
    if (s) go({ t: s.topic, s: id, d: search.d });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (corpus === undefined) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Загружаем регламенты…
      </div>
    );
  }
  if (!corpus) {
    return (
      <div className="max-w-2xl space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Регламенты</h1>
        <p className="text-sm text-muted-foreground">
          Регламенты ещё не загружены. Корпус собирается из документов агентства и кладётся в базу
          отдельно — обратитесь к администратору портала.
        </p>
      </div>
    );
  }

  const view: View = search.t ?? "handover";
  const section = search.s ? corpus.sections.find((x) => x.id === search.s) : undefined;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Регламенты</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Регламенты HR Comm и Digital в одном месте: передача проекта, документы, оплаты, закрытие.
          У каждого раздела — ссылка на исходный документ и его дата. Ивенты сюда не входят.
          {updatedAt ? ` Корпус собран ${ruDate(corpus.builtAt)}.` : null}
        </p>
      </div>

      <AskBar corpus={corpus} onSection={openSection} onConflicts={() => go({ t: "diff" })} />

      <div className="flex flex-col gap-5 lg:flex-row">
        <nav
          aria-label="Вкладки регламентов"
          className="flex gap-1 overflow-x-auto lg:w-56 lg:shrink-0 lg:flex-col lg:overflow-visible"
        >
          {TOPICS.map((t) => (
            <TabButton
              key={t.id}
              active={view === t.id}
              onClick={() => go({ t: t.id, d: search.d })}
              count={sectionsOf(corpus, t.id).length}
            >
              {t.label}
            </TabButton>
          ))}
          <div className="hidden h-px bg-border lg:my-2 lg:block" />
          <TabButton active={view === "bpmn"} onClick={() => go({ t: "bpmn" })} icon={GitBranch}>
            Схемы процессов
          </TabButton>
          <TabButton
            active={view === "diff"}
            onClick={() => go({ t: "diff" })}
            icon={AlertTriangle}
          >
            Расхождения
          </TabButton>
        </nav>

        <div className="min-w-0 flex-1">
          {view === "bpmn" ? (
            <ProcessesView
              corpus={corpus}
              active={search.p}
              onPick={(p) => go({ t: "bpmn", p })}
              onSection={openSection}
            />
          ) : view === "diff" ? (
            <DiscrepanciesView corpus={corpus} onSection={openSection} />
          ) : section ? (
            <SectionView
              section={section}
              onBack={() => go({ t: section.topic, d: search.d })}
              onSection={openSection}
            />
          ) : (
            <TopicView
              corpus={corpus}
              topic={view}
              direction={search.d}
              onDirection={(d) => go({ t: view, d })}
              onOpen={openSection}
              onScheme={(p) => go({ t: "bpmn", p })}
            />
          )}
        </div>
      </div>

      {corpus.missing.length ? (
        <p className="border-t pt-4 text-xs text-muted-foreground">
          Ещё не вошло, ждём от агентства: {corpus.missing.join("; ")}.
        </p>
      ) : null}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
  count,
  icon: Icon,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  count?: number;
  icon?: typeof GitBranch;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-left text-sm transition-colors lg:whitespace-normal",
        active
          ? "bg-primary/10 font-medium text-primary"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      {Icon ? <Icon className="size-4 shrink-0" /> : null}
      <span className="flex-1">{children}</span>
      {count !== undefined ? <span className="text-xs opacity-60">{count}</span> : null}
    </button>
  );
}

// ---------------------------------------------------------------------
// Вопрос к регламентам
// ---------------------------------------------------------------------

function AskBar({
  corpus,
  onSection,
  onConflicts,
}: {
  corpus: Corpus;
  onSection: (id: string) => void;
  onConflicts: () => void;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [asked, setAsked] = useState<string | null>(null);
  const [answer, setAnswer] = useState<RegulationsAnswer | null>(null);
  const speech = useSpeech((said) => setText((prev) => (prev ? `${prev} ${said}` : said)));

  async function ask(q: string) {
    const question = q.trim();
    if (!question || busy) return;
    speech.stop();
    setBusy(true);
    setAnswer(null);
    setAsked(question);
    try {
      const r = await askRegulationsFn({ data: { question } });
      if (!r.ok) {
        toast.error(r.error);
        return;
      }
      setAnswer(r.answer);
    } catch (e) {
      console.error("[regulations] вопрос не ушёл", e);
      toast.error("Не удалось получить ответ");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <form
          className="flex flex-col gap-2 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            void ask(text);
          }}
        >
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-3 size-4 text-muted-foreground" />
            <Input
              value={speech.listening && speech.interim ? speech.interim : text}
              readOnly={speech.listening}
              onChange={(e) => setText(e.target.value)}
              placeholder="Что хочешь узнать про регламенты?"
              aria-label="Вопрос к регламентам"
              className="h-10 pl-9"
              maxLength={1000}
            />
          </div>
          <div className="flex gap-2">
            {speech.supported && (
              <Button
                type="button"
                variant={speech.listening ? "default" : "outline"}
                className="h-10"
                onClick={speech.toggle}
                aria-label={speech.listening ? "Остановить диктовку" : "Спросить голосом"}
              >
                {speech.listening ? <Square className="size-4" /> : <Mic className="size-4" />}
              </Button>
            )}
            <Button type="submit" className="h-10" disabled={busy || !text.trim()}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
              Спросить
            </Button>
          </div>
        </form>
        {speech.error ? <p className="text-xs text-destructive">{speech.error}</p> : null}

        {!answer && !busy ? (
          <div className="flex flex-wrap gap-1.5">
            <span className="py-1 text-xs text-muted-foreground">Например:</span>
            {corpus.examples.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => {
                  setText(q);
                  void ask(q);
                }}
                className="rounded-full border px-3 py-1 text-xs transition-colors hover:bg-muted"
              >
                {q}
              </button>
            ))}
          </div>
        ) : null}

        {busy ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Ищу в регламентах: «{asked}»
          </p>
        ) : null}

        {answer && asked ? (
          <AnswerCard
            question={asked}
            answer={answer}
            onSection={onSection}
            onConflicts={onConflicts}
            onClose={() => {
              setAnswer(null);
              setText("");
            }}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

const COVERAGE = {
  full: {
    label: "Описано в регламентах",
    className: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100",
  },
  partial: {
    label: "Описано частично",
    className: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100",
  },
  none: {
    label: "В регламентах не описано",
    className: "bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-100",
  },
  conflict: {
    label: "Описано, но источники расходятся",
    className: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100",
  },
} as const;

function AnswerCard({
  question,
  answer,
  onSection,
  onConflicts,
  onClose,
}: {
  question: string;
  answer: RegulationsAnswer;
  onSection: (id: string) => void;
  onConflicts: () => void;
  onClose: () => void;
}) {
  const [draftOpen, setDraftOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  // «Описано» при двух разных правилах обещало бы ответ, которого нет.
  const cov =
    COVERAGE[answer.coverage === "full" && answer.conflicts.length ? "conflict" : answer.coverage];

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Не удалось скопировать — выделите текст вручную");
    }
  }

  return (
    <div className="space-y-3 rounded-lg border bg-muted/30 p-4" data-testid="regulations-answer">
      <div className="flex flex-wrap items-center gap-2">
        <span className={cn("rounded-md px-2 py-0.5 text-xs font-medium", cov.className)}>
          {cov.label}
        </span>
        <span className="text-xs text-muted-foreground">«{question}»</span>
        <button
          type="button"
          onClick={onClose}
          className="ml-auto text-xs text-muted-foreground hover:text-foreground"
        >
          Закрыть
        </button>
      </div>

      <MarkdownView text={answer.answer} onSection={onSection} />

      {answer.sections.length ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground">По разделам:</span>
          {answer.sections.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onSection(s.id)}
              className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-0.5 text-xs hover:bg-muted"
            >
              <BookOpen className="size-3" />
              {s.title}
            </button>
          ))}
        </div>
      ) : null}

      {answer.conflicts.length ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Источники расходятся:</span>
          {answer.conflicts.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={onConflicts}
              className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-900 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-100"
            >
              <AlertTriangle className="size-3" />
              {c.title}
            </button>
          ))}
        </div>
      ) : null}

      {answer.ask ? (
        <div className="space-y-2 rounded-md border bg-background p-3">
          {answer.gap ? <p className="text-sm">Не описано: {answer.gap}</p> : null}
          <p className="text-sm">
            Уточнить у: <span className="font-medium">{answer.ask.name}</span>
            <span className="text-muted-foreground"> — {answer.ask.role}</span>
          </p>
          {answer.draftQuestion ? (
            draftOpen ? (
              <div className="space-y-2">
                <Textarea readOnly value={answer.draftQuestion} rows={4} className="text-sm" />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void copy(answer.draftQuestion!)}
                >
                  {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                  {copied ? "Скопировано" : "Скопировать вопрос"}
                </Button>
              </div>
            ) : (
              <Button type="button" size="sm" variant="outline" onClick={() => setDraftOpen(true)}>
                Составить вопрос
              </Button>
            )
          ) : null}
        </div>
      ) : null}

      <p className="text-[11px] text-muted-foreground">
        Ответ собирает модель по тексту регламентов — сверяйтесь с разделом по ссылке.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------
// Вкладка, раздел, источник
// ---------------------------------------------------------------------

function TopicView({
  corpus,
  topic,
  direction,
  onDirection,
  onOpen,
  onScheme,
}: {
  corpus: Corpus;
  topic: TopicId;
  direction?: DirectionId;
  onDirection: (d?: DirectionId) => void;
  onOpen: (id: string) => void;
  onScheme: (id: string) => void;
}) {
  const all = sectionsOf(corpus, topic);
  const shown = direction ? all.filter((s) => s.direction === direction) : all;
  const schemes = corpus.processes.filter((p) => p.topic === topic);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="mr-2 text-lg font-semibold">{topicLabel(topic)}</h2>
        <FilterChip active={!direction} onClick={() => onDirection(undefined)}>
          Все
        </FilterChip>
        {DIRECTIONS.map((d) => (
          <FilterChip key={d.id} active={direction === d.id} onClick={() => onDirection(d.id)}>
            {d.label}
          </FilterChip>
        ))}
      </div>

      {schemes.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onScheme(p.id)}
          className="flex w-full items-center gap-3 rounded-lg border border-dashed px-4 py-3 text-left text-sm hover:bg-muted/50"
        >
          <GitBranch className="size-4 shrink-0 text-primary" />
          <span>
            <span className="font-medium">Схема BPMN: {p.title}</span>
            <span className="block text-xs text-muted-foreground">Открыть схему процесса</span>
          </span>
        </button>
      ))}

      {shown.length ? (
        <div className="grid gap-3 xl:grid-cols-2">
          {shown.map((s) => (
            <SectionCard key={s.id} section={s} onOpen={() => onOpen(s.id)} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          В этой вкладке по выбранному направлению разделов нет.
        </p>
      )}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-0.5 text-xs transition-colors",
        active ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}

function SectionCard({ section, onOpen }: { section: Section; onOpen: () => void }) {
  const preview = useMemo(
    () => plainText(section.body).replace(/\s+/g, " ").slice(0, 220),
    [section.body],
  );
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex flex-col gap-1.5 rounded-lg border bg-card p-4 text-left transition-colors hover:border-primary/40 hover:bg-muted/30"
    >
      <div className="flex items-center gap-2">
        <DirectionBadge id={section.direction} />
      </div>
      <span className="font-medium">{section.title}</span>
      <span className="line-clamp-3 text-xs text-muted-foreground">{preview}…</span>
      <span className="text-[11px] text-muted-foreground">{sourceLine(section.source)}</span>
    </button>
  );
}

function DirectionBadge({ id }: { id: string }) {
  const tone =
    id === "hr_comm"
      ? "bg-violet-100 text-violet-900 dark:bg-violet-950 dark:text-violet-100"
      : id === "digital"
        ? "bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-100"
        : "bg-muted text-muted-foreground";
  return (
    <span className={cn("rounded px-1.5 py-0.5 text-[11px] font-medium", tone)}>
      {directionLabel(id)}
    </span>
  );
}

function sourceLine(s: Source): string {
  return [s.title, s.date ? `ред. ${ruDate(s.date)}` : null].filter(Boolean).join(" · ");
}

function SectionView({
  section,
  onBack,
  onSection,
}: {
  section: Section;
  onBack: () => void;
  onSection: (id: string) => void;
}) {
  return (
    <article className="max-w-3xl space-y-4" data-testid="regulations-section">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" /> {topicLabel(section.topic)}
      </button>
      <div className="space-y-2">
        <DirectionBadge id={section.direction} />
        <h2 className="text-xl font-semibold tracking-tight">{section.title}</h2>
      </div>
      <SourceBox source={section.source} />
      <MarkdownView text={section.body} onSection={onSection} />
    </article>
  );
}

function SourceBox({ source }: { source: Source }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border bg-muted/30 px-3 py-2 text-xs">
      <span className="text-muted-foreground">Источник:</span>
      {source.url ? (
        <a
          href={source.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
        >
          {source.title} <ExternalLink className="size-3" />
        </a>
      ) : (
        <span className="font-medium">{source.title}</span>
      )}
      {source.date ? <span>редакция {ruDate(source.date)}</span> : null}
      {source.note ? <span className="text-muted-foreground">{source.note}</span> : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// Схемы и расхождения
// ---------------------------------------------------------------------

function ProcessesView({
  corpus,
  active,
  onPick,
  onSection,
}: {
  corpus: Corpus;
  active?: string;
  onPick: (id: string) => void;
  onSection: (id: string) => void;
}) {
  const proc = corpus.processes.find((p) => p.id === active) ?? corpus.processes[0];
  const xml = useMemo(() => (proc ? toBpmnXml(proc) : ""), [proc]);
  const size = useMemo(() => {
    if (!proc) return { width: 1, height: 1 };
    const L = layout(proc);
    return { width: L.width, height: L.height };
  }, [proc]);
  if (!proc) return <p className="text-sm text-muted-foreground">Схем пока нет.</p>;

  const conflicts = proc.nodes
    .filter((n) => n.conflict)
    .map((n) => ({ node: n, d: corpus.discrepancies.find((d) => d.id === n.conflict) }));
  const sections = [...new Set(proc.nodes.map((n) => n.section).filter(Boolean))] as string[];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {corpus.processes.map((p) => (
          <FilterChip key={p.id} active={p.id === proc.id} onClick={() => onPick(p.id)}>
            {p.title}
          </FilterChip>
        ))}
      </div>
      <BpmnView key={proc.id} xml={xml} fileName={proc.id} size={size} />
      <p className="text-xs text-muted-foreground">Собрано из: {proc.basedOn}</p>
      {conflicts.length ? (
        <div className="space-y-1.5">
          <p className="text-sm font-medium">⚠ — шаг, где источники расходятся:</p>
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {conflicts.map(({ node, d }) => (
              <li key={node.id}>
                <span className="font-medium">{node.name}</span>
                {d ? <span className="text-muted-foreground"> — {d.title}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {sections.length ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Разделы:</span>
          {sections.map((id) => {
            const s = corpus.sections.find((x) => x.id === id);
            return s ? (
              <button
                key={id}
                type="button"
                onClick={() => onSection(id)}
                className="rounded-md border px-2 py-0.5 text-xs hover:bg-muted"
              >
                {s.title}
              </button>
            ) : null;
          })}
        </div>
      ) : null}
    </div>
  );
}

function DiscrepanciesView({
  corpus,
  onSection,
}: {
  corpus: Corpus;
  onSection: (id: string) => void;
}) {
  const owner = (id: string) => corpus.contacts.find((p) => p.id === id);
  return (
    <div className="max-w-4xl space-y-4" data-testid="regulations-diff">
      <div>
        <h2 className="text-lg font-semibold">Карта расхождений</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Места, где регламенты бухгалтерии, Digital и чек-лист HR Comm говорят разное. Пока решения
          нет, справочная приводит оба варианта и называет, кто решает. После решения раздел
          правится в одном месте и уходит отсюда.
        </p>
      </div>
      {corpus.discrepancies.map((d, i) => {
        const who = owner(d.owner);
        return (
          <Card key={d.id} id={`diff-${d.id}`}>
            <CardContent className="space-y-3 py-4">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-xs text-muted-foreground">{i + 1}.</span>
                <h3 className="font-medium">{d.title}</h3>
                <span className="text-xs text-muted-foreground">{topicLabel(d.topic)}</span>
              </div>
              <div className="divide-y rounded-md border">
                {d.variants.map((v, j) => (
                  <div key={j} className="grid gap-1 px-3 py-2 text-sm sm:grid-cols-[180px_1fr]">
                    <span className="text-xs font-medium text-muted-foreground">
                      {v.section ? (
                        <button
                          type="button"
                          className="text-left text-primary hover:underline"
                          onClick={() => onSection(v.section!)}
                        >
                          {v.source}
                        </button>
                      ) : (
                        v.source
                      )}
                    </span>
                    <span>{v.says}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm">
                <span className="font-medium">Вопрос на решение:</span> {d.question}
              </p>
              {who ? (
                <p className="text-xs text-muted-foreground">
                  Решает: {who.name}, {who.role}
                </p>
              ) : null}
            </CardContent>
          </Card>
        );
      })}

      {corpus.notes.map((n) => (
        <Card key={n.title}>
          <CardContent className="space-y-2 py-4">
            <h3 className="font-medium">{n.title}</h3>
            <SourceBox source={n.source} />
            <MarkdownView text={n.body} />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
